import { useCallback, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { deleteFile } from "api";
import errorToast from "helpers/errorToast";
import { generateUUID, promiseAllSettled } from "helpers/commonHelpers";
import { AttachmentBucket } from "types/Common";

import { useFileUpload, UploadResult } from "@RequestFormV2/sections/Provider/hooks/useFileUpload";
import { useProviderHandlers } from "@RequestFormV2/sections/Provider/context/useProviderHandlers";
import { BucketFileEntry, OtherFileState } from "@RequestFormV2/sections/Provider/hooks/attachmentTypes";
import {
  hydrateOtherFilesFromForm,
  migrateOtherAttachmentsToBucket,
} from "@RequestFormV2/sections/Provider/utils/otherBucketAttachmentUtils";

function getFileRecordKey(file: OtherFileState): string | number {
  return (file.file as string | undefined) ?? file.id;
}

export function useOtherAttachments(otherBucket?: AttachmentBucket) {
  const { getValues, setValue } = useFormContext();
  const bucketAttachmentsForm = useWatch({ name: "bucket_attachments" }) as BucketFileEntry[] | undefined;
  const otherAttachmentsForm = useWatch({ name: "other_attachments" }) as OtherFileState[] | undefined;
  const { upload } = useFileUpload();
  const { handleAppendBucketAttachments, handleRemoveBucketFileByRef, handleAppendOtherAttachments, handleRemoveOtherFile } =
    useProviderHandlers();

  const [files, setFiles] = useState<Record<string | number, OtherFileState>>(() =>
    hydrateOtherFilesFromForm(
      otherBucket,
      (getValues("bucket_attachments") as BucketFileEntry[]) || [],
      getValues("other_attachments") as OtherFileState[] | undefined,
    ),
  );

  useEffect(() => {
    if (!otherBucket || !otherAttachmentsForm?.length) return;

    const existingBucketAttachments = bucketAttachmentsForm ?? [];
    const migrated = migrateOtherAttachmentsToBucket(otherBucket, existingBucketAttachments, otherAttachmentsForm);

    if (migrated.length) {
      setValue("bucket_attachments", [...existingBucketAttachments, ...migrated]);
      setValue("other_attachments", []);
    }
  }, [otherBucket, otherAttachmentsForm, bucketAttachmentsForm, setValue]);

  useEffect(() => {
    const bucketAttachments = bucketAttachmentsForm ?? [];
    const otherAttachments = otherAttachmentsForm ?? [];

    if (!bucketAttachments.length && !otherAttachments.length) return;

    setFiles((prev) => {
      if (Object.keys(prev).length) return prev;
      return hydrateOtherFilesFromForm(otherBucket, bucketAttachments, otherAttachments);
    });
  }, [otherBucket, bucketAttachmentsForm, otherAttachmentsForm]);

  const updateFile = useCallback((id: string | number, data: Partial<OtherFileState>) => {
    setFiles((prev) => ({
      ...prev,
      ...(prev[id] && { [id]: { ...prev[id], ...data } }),
    }));
  }, []);

  const uploadSingleFile = useCallback(
    (data: OtherFileState): Promise<UploadResult> | null => {
      if (!data.file || typeof data.file === "string") return null;
      return upload(data.file, (pct) => updateFile(data.id, { progress: pct }), data.abortController?.signal)
        .then((res) => {
          setFiles((prev) => {
            const cloned = { ...prev };
            const key = otherBucket ? res.reference_number : res.id;
            delete cloned[data.id];
            const uploadedFile: OtherFileState = {
              id: otherBucket ? otherBucket.id : res.id,
              file: otherBucket ? res.reference_number : undefined,
              filename: otherBucket ? res.name : undefined,
              bucket_name: otherBucket?.bucket_name,
              reference_number: res.reference_number,
              name: res.name,
              url: res.url,
              progress: 0,
              error: false,
              errorMessage: "",
            };
            return {
              ...cloned,
              [key]: uploadedFile,
            };
          });
          return res;
        })
        .catch((error: { code?: string; message?: string }) => {
          if (error?.code !== "ERR_CANCELED") errorToast(error);
          updateFile(data.id, { progress: 0, error: true, errorMessage: error.message || "" });
          return Promise.reject(error);
        });
    },
    [otherBucket, upload, updateFile],
  );

  const handleFilesSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = event.target.files;
      if (!selectedFiles) return;

      const filesData: Record<string, OtherFileState> = {};
      Array.from(selectedFiles).forEach((file) => {
        const uuid = generateUUID();
        filesData[uuid] = {
          id: uuid,
          file,
          progress: 1,
          error: false,
          errorMessage: "",
          name: file.name,
          url: "",
          abortController: new AbortController(),
        };
      });

      setFiles((prev) => ({ ...prev, ...filesData }));
      event.target.value = "";

      const uploads = Object.values(filesData).map((f) => uploadSingleFile(f)).filter(Boolean) as Promise<UploadResult>[];
      const results = await promiseAllSettled(uploads);
      const uploaded = results.filter((r) => r.status === "fulfilled").map((r) => (r as { status: string; value: UploadResult }).value);

      if (otherBucket) {
        const formatted: BucketFileEntry[] = uploaded.map((f) => ({
          bucket_name: otherBucket.bucket_name,
          id: otherBucket.id,
          file: f.reference_number,
          filename: f.name,
          url: f.url,
        }));
        handleAppendBucketAttachments(formatted);
      } else {
        handleAppendOtherAttachments(uploaded);
      }
    },
    [otherBucket, uploadSingleFile, handleAppendBucketAttachments, handleAppendOtherAttachments],
  );

  const resolveStateKey = (prev: Record<string | number, OtherFileState>, file: OtherFileState) => {
    const fileKey = getFileRecordKey(file);
    return prev[fileKey] ? fileKey : file.id;
  };

  const handleRemove = useCallback(
    async (file: OtherFileState) => {
      if (file.progress > 0 && file.abortController) {
        file.abortController.abort();
        setFiles((prev) => {
          const stateKey = resolveStateKey(prev, file);
          const cloned = { ...prev };
          delete cloned[stateKey];
          return cloned;
        });
        return;
      }

      setFiles((prev) => {
        const stateKey = resolveStateKey(prev, file);
        if (!prev[stateKey]) return prev;
        return { ...prev, [stateKey]: { ...prev[stateKey], isDeleting: true } };
      });

      const referenceNumber = otherBucket
        ? (file.file as string | undefined)
        : (file.reference_number ?? (file.file as string | undefined));

      if (referenceNumber && !file.error) {
        try {
          await deleteFile(referenceNumber);
        } catch (e: unknown) {
          const err = e as { response?: { status: number } };
          if (err?.response?.status !== 404) {
            setFiles((prev) => {
              const stateKey = resolveStateKey(prev, file);
              if (!prev[stateKey]) return prev;
              return { ...prev, [stateKey]: { ...prev[stateKey], isDeleting: false } };
            });
            errorToast(e, "Could not delete file, please try again later");
            return;
          }
        }
      }

      setFiles((prev) => {
        const stateKey = resolveStateKey(prev, file);
        const cloned = { ...prev };
        delete cloned[stateKey];
        return cloned;
      });

      if (otherBucket) {
        const fileRef = typeof file.file === "string" ? file.file : String(file.file ?? "");
        if (fileRef) handleRemoveBucketFileByRef(fileRef);
      } else {
        handleRemoveOtherFile(file.id);
      }
    },
    [otherBucket, handleRemoveBucketFileByRef, handleRemoveOtherFile],
  );

  return {
    files: Object.values(files),
    handleFilesSelect,
    handleRemove,
  };
}
